import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { useUser } from "../utils/userContext";
import { createApiClient } from "../utils/apiClient";
import FeatureGate from "../utils/featureGate";
import { useTier } from "../utils/tierContext";
import dayjs from "dayjs";
import styles from "./Approvals.module.css";
import Toast from "./Toast";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Expense {
  expenseId: number;
  expenseType: string;
  amount: number;
}

interface ApprovalChainDisplayModel {
  id: number;
  timeId: number;
  userId: number;
  firstName: string;
  lastName: string;
  projectId: number;
  projectName: string;
  description: string;
  legalDescription: string;
  workDate: string;
  hours: number;
  jobName: string;
  invoiceId: number;
  businessName: string;
  approverId: number;
  approverType: string;
  stepOrder: number;
  status: string;
  comments: string | null;
  createdAt: string;
  respondedAt: string | null;
  expenses: Expense[];
}

interface ApprovalChainModel {
  id: number;
  timeId: number;
  approverId: number;
  approverType: string;
  stepOrder: number;
  status: string;
  comments: string | null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const Approvals = () => {
  const authRequest = { ...loginRequest };
  const location = useLocation();
  const nav = useNavigate();
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [approvals, setApprovals] = useState<ApprovalChainDisplayModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [submitting, setSubmitting] = useState<number | null>(null);

  const { isVisible } = useTier();
  const showClient = isVisible("approvals_show_client");
  const showProspect = isVisible("approvals_show_prospect");
  const showLegal = isVisible("approvals_show_legal");
  const showJob = isVisible("approvals_show_job");
  const showDescription = isVisible("approvals_show_description");

  const [status, setStatus] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [highlightTimeId, setHighlightTimeId] = useState<number | null>(null);

  // Read navigation state from notification click-through
  useEffect(() => {
    const state = location.state as { timeId?: number } | null;
    if (!state?.timeId) return;
    setHighlightTimeId(state.timeId);
    nav(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to and auto-clear highlighted row after data loads
  useEffect(() => {
    if (!highlightTimeId || loading) return;

    const scrollTimer = setTimeout(() => {
      const el = document.querySelector(`[data-time-id="${highlightTimeId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);

    const clearTimer = setTimeout(() => {
      setHighlightTimeId(null);
    }, 3500);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightTimeId, loading]);

  const showStatus = useCallback((nextStatus: string, message: string) => {
    setStatus(nextStatus);
    setStatusMessage(message);
  }, []);

  /* ---- Fetch approvals ---- */
  const loadApprovals = useCallback(async () => {
    if (!api || !user?.userId) return;
    setLoading(true);

    try {
      const data = await api.get(`Approvals`);
      setApprovals(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err?.status === 404) {
        setApprovals([]);
      } else {
        console.error("Error loading approvals:", err);
        showStatus("error", err?.message || "Failed to load approvals.");
      }
    } finally {
      setLoading(false);
    }
  }, [api, user?.userId, showStatus]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  /* ---- Expand / collapse expense rows ---- */
  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ---- Approve ---- */
  const handleApprove = async (item: ApprovalChainDisplayModel) => {
    if (!api) return;
    setSubmitting(item.id);

    const body: ApprovalChainModel = {
      id: item.id,
      timeId: item.timeId,
      approverId: item.approverId,
      approverType: item.approverType,
      stepOrder: item.stepOrder,
      status: "approved",
      comments: null,
    };

    try {
      await api.put("Approvals", body);
      showStatus("success", `Approved time entry for ${item.firstName} ${item.lastName}.`);
      setApprovals((prev) => prev.filter((a) => a.id !== item.id));
    } catch (err: any) {
      console.error("Error approving:", err);
      showStatus("error", err?.message || "Failed to approve. Please try again.");
    } finally {
      setSubmitting(null);
    }
  };

  /* ---- Reject ---- */
  const startReject = (id: number) => {
    setRejectingId(id);
    setRejectComment("");
  };

  const cancelReject = () => {
    setRejectingId(null);
    setRejectComment("");
  };

  const confirmReject = async (item: ApprovalChainDisplayModel) => {
    if (!api || !rejectComment.trim()) return;
    setSubmitting(item.id);

    const body: ApprovalChainModel = {
      id: item.id,
      timeId: item.timeId,
      approverId: item.approverId,
      approverType: item.approverType,
      stepOrder: item.stepOrder,
      status: "rejected",
      comments: rejectComment.trim(),
    };

    try {
      await api.put("Approvals", body);
      showStatus("success", `Rejected time entry for ${item.firstName} ${item.lastName}.`);
      setApprovals((prev) => prev.filter((a) => a.id !== item.id));
      cancelReject();
    } catch (err: any) {
      console.error("Error rejecting:", err);
      showStatus("error", err?.message || "Failed to reject. Please try again.");
    } finally {
      setSubmitting(null);
    }
  };

  /* ---- Helpers ---- */
  const expensesTotal = (expenses: Expense[]) =>
    expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const formatCurrency = (val: number) =>
    val.toLocaleString("en-US", { style: "currency", currency: "USD" });

  /* ---- Render ---- */
  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
      >
        <FeatureGate
          feature="approval_workflow"
          fallback={
            <div className="main-articles company-articles">
              <div className={styles["approvals-shell"]}>
                <div className={styles["approvals-card"]}>
                  <div className={styles["approvals-card-body"]}>
                    <div className={styles["approvals-empty"]}>
                      <div className={styles["approvals-empty__title"]}>Approval Workflow</div>
                      <div className={styles["approvals-empty__subtitle"]}>This feature requires the Core plan or higher.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        >
        <>
          <div className="main-articles company-articles">
            <div className={styles["approvals-shell"]}>
              <div className={styles["approvals-card"]}>
                <div className={styles["approvals-card-header"]}>
                  <h2 className={styles["approvals-card-header-title"]}>Pending Approvals</h2>
                </div>
                <div className={styles["approvals-card-body"]}>
                  {loading ? (
                    <div className={styles["approvals-table-wrap"]}>
                      <table className={styles["approvals-table"]}>
                        <thead>
                          <tr>
                            <th /><th>Employee</th>{showClient && <th>Client</th>}{showProspect && <th>Prospect</th>}{showLegal && <th>Legal</th>}
                            <th>Date</th><th>Hours</th>{showJob && <th>Job Type</th>}<th>Expenses Total</th><th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[1, 2, 3].map((i) => (
                            <tr key={i} style={{ opacity: 0.5 }}>
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 16, height: 16, borderRadius: 4 }} /></td>
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 100, height: 13 }} /></td>
                              {showClient && <td><span className={styles["approvals-skeleton"]} style={{ width: 90, height: 13 }} /></td>}
                              {showProspect && <td><span className={styles["approvals-skeleton"]} style={{ width: 110, height: 13 }} /></td>}
                              {showLegal && <td><span className={styles["approvals-skeleton"]} style={{ width: 130, height: 13 }} /></td>}
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 70, height: 13 }} /></td>
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 40, height: 13 }} /></td>
                              {showJob && <td><span className={styles["approvals-skeleton"]} style={{ width: 80, height: 13 }} /></td>}
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 60, height: 13 }} /></td>
                              <td><span className={styles["approvals-skeleton"]} style={{ width: 70, height: 13 }} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : approvals.length === 0 ? (
                    <div className={styles["approvals-empty"]}>
                      <svg className={styles["approvals-empty__icon"]} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="20" stroke="#d1d5db" strokeWidth="1.5" />
                        <path d="M16 24l5 5 11-11" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className={styles["approvals-empty__title"]}>No Pending Approvals</div>
                      <div className={styles["approvals-empty__subtitle"]}>You're all caught up — no time entries need your review.</div>
                    </div>
                  ) : (
                    <div className={styles["approvals-table-wrap"]}>
                      <table className={styles["approvals-table"]}>
                        <thead>
                        <tr>
                          <th />
                          <th>Employee</th>
                          {showClient && <th>Client</th>}
                          {showProspect && <th>Prospect</th>}
                          {showLegal && <th>Legal</th>}
                          <th>Date</th>
                          <th>Hours</th>
                          {showJob && <th>Job Type</th>}
                          <th>Expenses Total</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      {approvals.map((item) => (
                        <tbody key={item.id} data-testid={`approval-row-${item.id}`} data-time-id={item.timeId}
                          className={highlightTimeId === item.timeId ? styles["approvals-row-highlight"] : undefined}
                        >
                          {/* Main row */}
                          <tr
                            className={item.description ? styles["approvals-row--has-description"] : undefined}
                            style={item.expenses && item.expenses.length > 0 ? { cursor: "pointer" } : undefined}
                            onClick={() => {
                              if (item.expenses && item.expenses.length > 0) toggleExpand(item.id);
                            }}
                          >
                            <td>
                              {item.expenses && item.expenses.length > 0 && (
                                <button
                                  type="button"
                                  className={styles["approvals-expand-btn"]}
                                  aria-label={expandedRows.has(item.id) ? "Collapse expenses" : "Expand expenses"}
                                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit", color: "inherit" }}
                                  onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                                >
                                  {expandedRows.has(item.id) ? "−" : "+"}
                                </button>
                              )}
                            </td>
                            <td>{item.firstName} {item.lastName}</td>
                            {showClient && <td>{item.businessName}</td>}
                            {showProspect && <td>{item.projectName}</td>}
                            {showLegal && <td>{item.legalDescription || "—"}</td>}
                            <td>{dayjs(item.workDate).format("M/D/YYYY")}</td>
                            <td>{item.hours}</td>
                            {showJob && <td>{item.jobName}</td>}
                            <td>
                              <span className={styles["approvals-expenses-cell"]}>
                                {item.expenses && item.expenses.length > 0
                                  ? formatCurrency(expensesTotal(item.expenses))
                                  : "—"}
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {rejectingId === item.id ? (
                                <div className={styles["approvals-reject-form"]}>
                                  <input
                                    type="text"
                                    className={styles["approvals-reject-input"]}
                                    placeholder="Reason for rejection…"
                                    value={rejectComment}
                                    onChange={(e) => setRejectComment(e.target.value)}
                                    aria-label="Reason for rejection"
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    className={styles["approvals-btn-confirm"]}
                                    disabled={!rejectComment.trim() || submitting === item.id}
                                    onClick={() => confirmReject(item)}
                                    data-testid={`confirm-reject-${item.id}`}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    className={styles["approvals-btn-cancel"]}
                                    disabled={submitting === item.id}
                                    onClick={cancelReject}
                                    data-testid={`cancel-reject-${item.id}`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className={styles["approvals-actions"]}>
                                  <button
                                    type="button"
                                    className={styles["approvals-btn-approve"]}
                                    disabled={submitting === item.id}
                                    onClick={() => handleApprove(item)}
                                    data-testid={`approve-${item.id}`}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    className={styles["approvals-btn-reject"]}
                                    disabled={submitting === item.id}
                                    onClick={() => startReject(item.id)}
                                    data-testid={`reject-${item.id}`}
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>

                          {/* Description sub-row */}
                          {showDescription && item.description && (
                            <tr className={styles["approvals-description-row"]} data-testid={`approval-description-${item.id}`}>
                              <td />
                              <td colSpan={99} className={styles["approvals-description-cell"]}>
                                {item.description}
                              </td>
                            </tr>
                          )}

                          {/* Expense detail rows */}
                          {expandedRows.has(item.id) &&
                            item.expenses.map((exp) => (
                              <tr key={exp.expenseId} className={styles["approvals-expense-row"]} data-testid={`approval-expense-${item.id}-${exp.expenseId}`}>
                                <td />
                                <td colSpan={2}>
                                  <span className={styles["approvals-expense-label"]}>
                                    {exp.expenseType}
                                  </span>
                                </td>
                                <td colSpan={4}>{formatCurrency(exp.amount)}</td>
                                <td colSpan={2} />
                              </tr>
                            ))}
                        </tbody>
                      ))}
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
        </FeatureGate>
      </MsalAuthenticationTemplate>
      {status && (
        <Toast
          message={statusMessage}
          type={status as "success" | "error"}
          onClose={() => {
            setStatus(null);
            setStatusMessage("");
          }}
        />
      )}
    </>
  );
};

export default Approvals;
